import React, { useState, useEffect } from "react";
import {
    Box,
    Typography,
    Card,
    CardContent,
    Button,
    CircularProgress,
    Alert,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    Divider
} from "@mui/material";
import Icon from "@/theme/vectors/bundle";
import { useTranslation } from "@/hooks/useTranslation";
import { useTableList } from "@/hooks/database/useTableList";
import TableViewPanel from "./TableViewPanel";

const DataTablesPanel = () => {
    const { t } = useTranslation();
    const { 
        tables, 
        loadingTables, 
        tablesError, 
        fetchTables 
    } = useTableList();
    
    // Table view slide panel
    const [isViewPanelOpen, setIsViewPanelOpen] = useState(false);
    const [selectedTable, setSelectedTable] = useState(null);
    
    // Fetch tables when the component mounts
    useEffect(() => {
        fetchTables();
    }, [fetchTables]);
    
    const handleTableClick = (tableName) => {
        setSelectedTable(tableName);
        setIsViewPanelOpen(true);
    };
    
    const handleClosePanel = () => {
        setIsViewPanelOpen(false);
    };

    return (
        <>
            <Card>
                <CardContent>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                        <Typography variant="h6">
                            {t('database.tables.heading')}
                        </Typography>
                        <Button 
                            variant="outlined" 
                            color="primary" 
                            onClick={fetchTables} 
                            disabled={loadingTables}
                            startIcon={<Icon name="refresh2" size={20} />}
                            size="small"
                        >
                            {t('common.refresh')}
                        </Button>
                    </Box>
                    
                    {loadingTables ? (
                        <Box display="flex" justifyContent="center" py={3}>
                            <CircularProgress />
                        </Box>
                    ) : tablesError ? (
                        <Alert severity="error">{t('database.tables.fetchError')}</Alert>
                    ) : (
                        <List>
                            {tables.length === 0 ? (
                                <Typography color="text.secondary">{t('database.tables.noTablesFound')}</Typography>
                            ) : (
                                tables.map((table, index) => (
                                    <React.Fragment key={index}>
                                        <ListItem 
                                            button 
                                            onClick={() => handleTableClick(table.name)}
                                            sx={{
                                                '&:hover': {
                                                    backgroundColor: '#f5f5f5',
                                                }
                                            }}
                                        >
                                            <ListItemIcon>
                                                <Icon name="fileContent" size={20} />
                                            </ListItemIcon>
                                            <ListItemText 
                                                primary={table.name} 
                                            />
                                        </ListItem>
                                        {index < tables.length - 1 && <Divider />}
                                    </React.Fragment>
                                ))
                            )}
                        </List>
                    )}
                </CardContent>
            </Card>
            
            {/* Table Data View Panel */}
            {selectedTable && (
                <TableViewPanel
                    tableName={selectedTable}
                    isOpen={isViewPanelOpen}
                    onClose={handleClosePanel}
                />
            )}
        </>
    );
};

export default DataTablesPanel;